import Immutable from 'immutable';

import type {
    Blueprint,
    BlueprintAutoPickResponse,
    BlueprintsListData,
    NarrativeThreadEnum,
    PublicBlueprintsListData,
    PublicBlueprintUseCase,
} from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { setBlueprintId, setRecommendedBlueprint } from 'js/src/data/app';
import {
    getBlueprint,
    getCustomBlueprint,
    pushBlueprint,
    pushBlueprints,
    setBlueprintName,
    setBlueprintsLoading,
} from 'js/src/data/blueprints';
import { loadLuminaryPreviewsOperation } from 'js/src/data/thumbnail-previews';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import WebSocketClient from 'js/src/libs/websocket-client';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Generates Luminary thumbnails for blueprints that don't have a poster_url by fetching video_data
 */
export const generateBlueprintThumbnailsOperation = createLuminaryOperation(
    async (
        { call },
        {
            blueprints,
            includeVideoIdsForPosterUpload,
        }: { blueprints: Blueprint[]; includeVideoIdsForPosterUpload?: boolean },
    ): Promise<void> => {
        const blueprintsNeedingThumbnails = blueprints.filter((b) => !b.poster_url);
        if (blueprintsNeedingThumbnails.length === 0) return;

        try {
            const ids = blueprintsNeedingThumbnails.map((b) => b.id).join(',');
            const videoDataResponse = await ApiService.blueprintsVideoDataRetrieve({
                query: { ids },
            });

            const videoDataMap = videoDataResponse.data as Record<string, string>;
            const blueprintsWithVideoData = blueprintsNeedingThumbnails.filter(
                (blueprint) => videoDataMap[blueprint.id],
            );

            if (blueprintsWithVideoData.length === 0) return;

            const videoDataList = blueprintsWithVideoData.map((blueprint) =>
                JSON.parse(videoDataMap[blueprint.id]),
            );
            const blueprintIds = Immutable.List(blueprintsWithVideoData.map((b) => b.id));

            const videoIdsForPosterUpload = includeVideoIdsForPosterUpload
                ? Immutable.List(blueprintsWithVideoData.map((b) => b.video_ids?.[0]))
                : undefined;

            call(loadLuminaryPreviewsOperation, {
                newVideoDataList: videoDataList,
                newIdList: blueprintIds,
                videoIdsForPosterUpload,
            });
        } catch {
            // Silently fail - blueprints will just show sparkle icon
        }
    },
);

/**
 * Fetches and sets the available public blueprints.
 * @param narrative_thread the narrative thread of the video we're creating
 * @param template the template of the video we're creating
 * @param max_results the maximum number of blueprints to fetch
 * @param ids the ids of the blueprints to fetch
 * @param use_case the use case of the blueprints to fetch
 */
export const fetchPublicBlueprints = ({
    narrative_thread,
    template,
    max_results,
    ids,
    use_case,
}: {
    narrative_thread?: string;
    template?: string;
    max_results?: number;
    ids?: number[];
    use_case?: PublicBlueprintUseCase;
}) => {
    return async (dispatch: AppDispatch) => {
        // dedupe ids if set
        if (ids && ids.length > 0) {
            ids = Array.from(new Set(ids));
        }
        try {
            const query: PublicBlueprintsListData['query'] = {
                ...(narrative_thread && { narrative_thread }),
                ...(template && { template }),
                ...(max_results && { max_results }),
                ...(ids && { ids: ids ? ids.join(',') : undefined }),
                ...(use_case && { use_case }),
            };
            const response = await ApiService.publicBlueprintsList({ query });
            dispatch(pushBlueprints({ blueprints: response.data }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    };
};

/**
 * Fetches and sets the available public & custom blueprints.
 * @param narrative_thread the narrative thread of the video we're creating
 * @param template the template of the video we're creating
 * @param max_results the maximum number of blueprints to fetch
 * @param ids the ids of the blueprints to fetch
 * @param use_case the use case of the blueprints to fetch
 */
export const fetchBlueprints = ({
    narrative_thread,
    template,
    max_results,
    ids,
    use_case,
}: {
    narrative_thread?: string;
    template?: string;
    max_results?: number;
    ids?: number[];
    use_case?: PublicBlueprintUseCase;
}) => {
    return async (dispatch: AppDispatch) => {
        // dedupe ids if set
        if (ids && ids.length > 0) {
            ids = Array.from(new Set(ids));
        }
        try {
            const query: BlueprintsListData['query'] = {
                ...(narrative_thread && { narrative_thread }),
                ...(template && { template }),
                ...(max_results && { max_results }),
                ...(ids && { ids: ids ? ids.join(',') : undefined }),
                ...(use_case && { use_case }),
            };
            const response = await ApiService.blueprintsList({ query });
            dispatch(pushBlueprints({ blueprints: response.data }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    };
};

export const getOrFetchBlueprint = (blueprintId: number) => {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<Blueprint | null> => {
        const blueprint = getCustomBlueprint(getState(), blueprintId);
        if (blueprint) {
            return blueprint;
        }
        try {
            const response = await ApiService.blueprintsRetrieve({
                path: { id: blueprintId },
            });
            const blueprint = response.data;
            dispatch(pushBlueprint({ blueprint }));
            return blueprint;
        } catch (e: unknown) {
            ErrorReporter.capture(e);
            return null;
        }
    };
};

/**
 * Returns the provided blueprint id if it's valid and matches the specified narrative thread, otherwise null.
 * This is useful when making video recommendation requests where we only want to use the blueprint id if it matches
 * the narrative thread of the request.
 */
export const blueprintIdIfItMatchesNarrativeThread = (
    blueprintId: number | null,
    narrativeThread: NarrativeThreadEnum,
) => {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<number | null> => {
        if (blueprintId === null) {
            return null;
        }
        const blueprint = await dispatch(getOrFetchBlueprint(blueprintId));
        if (blueprint === null) {
            // can't find details, assume not valid
            return null;
        }
        if (blueprint.narrative_thread !== narrativeThread) {
            // mismatch!
            return null;
        }
        return blueprintId;
    };
};

export function updateBlueprintName({ blueprintId, name }: { blueprintId: number; name: string }) {
    return (dispatch: AppDispatch) => {
        ApiService.customBlueprintsPartialUpdate({
            path: { id: blueprintId },
            body: {
                name,
            },
        })
            .then((response) => {
                dispatch(
                    setBlueprintName({
                        name: response.data.name,
                        blueprintId,
                    }),
                );
            })
            .catch((e: unknown) => {
                ErrorReporter.capture(e);
                Toast.error(
                    'Something went wrong with renaming your blueprint, please try again later.',
                );
            });
    };
}

// Create a new customer blueprint from a source video. The child video of the blueprint is an archived duplicate of the source video.
export const createCustomerBlueprintOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        {
            videoId,
            name,
            loadPreview,
            customInstructions,
        }: { videoId: number; name: string; loadPreview?: boolean; customInstructions?: string },
    ) => {
        try {
            const response = await ApiService.videosDuplicateVideosCreate({
                body: {
                    video_ids: [videoId],
                    with_prefix: true,
                    archived: true,
                },
            });
            const video = response.data[0];
            const { data: blueprint } = await ApiService.customBlueprintsCreateFromSimilarCreate({
                body: {
                    video_id: video.id,
                    archived: false,
                    name,
                    custom_instructions: customInstructions,
                },
            });
            dispatch(pushBlueprint({ blueprint }));
            if (loadPreview) {
                await call(generateBlueprintThumbnailsOperation, {
                    blueprints: [blueprint],
                    includeVideoIdsForPosterUpload: true,
                });
            }
            return blueprint;
        } catch (error) {
            ErrorReporter.capture(error);
            Toast.error('Error creating Blueprint from video. Please try again.');
            return null;
        }
    },
);

/**
 * Fetches customer blueprints
 * Only returns blueprints that are not archived and not temporary
 * Also loads luminary previews for blueprint thumbnails using the video_data fetched separately
 */
export const loadCustomerBlueprintsOperation = createLuminaryOperation(
    async ({ dispatch, call }, renderPreviews = true) => {
        dispatch(setBlueprintsLoading({ loading: true }));

        try {
            const response = await ApiService.customBlueprintsList({
                query: {
                    archived: false,
                    temporary: false,
                },
            });

            dispatch(pushBlueprints({ blueprints: response.data }));

            if (renderPreviews && response.data.length > 0) {
                await call(generateBlueprintThumbnailsOperation, {
                    blueprints: response.data,
                    includeVideoIdsForPosterUpload: true,
                });
            }

            dispatch(setBlueprintsLoading({ loading: false }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            dispatch(setBlueprintsLoading({ loading: false }));
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    },
);

/**
 * Fetches public blueprints filtered by use_case and loads luminary previews for blueprint thumbnails.
 * This operation should be used when you need to display public blueprint previews (e.g., in a modal).
 */
export const loadPublicBlueprintsOperation = createLuminaryOperation(
    async ({ dispatch, call }, use_case?: PublicBlueprintUseCase) => {
        try {
            const query: PublicBlueprintsListData['query'] = {
                ...(use_case && { use_case }),
            };
            const response = await ApiService.publicBlueprintsList({ query });

            dispatch(pushBlueprints({ blueprints: response.data }));

            if (response.data.length > 0) {
                await call(generateBlueprintThumbnailsOperation, { blueprints: response.data });
            }

            return { success: true, blueprints: response.data };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    },
);

/**
 * Fetches a single blueprint by ID and generates its Luminary preview if needed.
 * Use this when you need to display a blueprint preview for a specific blueprint (e.g., from cookie).
 */
export const fetchBlueprintWithPreviewOperation = createLuminaryOperation(
    async ({ dispatch, call, getState }, blueprintId: number) => {
        try {
            let blueprint = getBlueprint(getState(), blueprintId);

            if (!blueprint) {
                const response = await ApiService.blueprintsRetrieve({
                    path: { id: blueprintId },
                });
                blueprint = response.data;
                dispatch(pushBlueprint({ blueprint }));
            }

            if (blueprint) {
                await call(generateBlueprintThumbnailsOperation, { blueprints: [blueprint] });
            }

            return { success: true, blueprint };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprint: null };
        }
    },
);

/**
 * Automatically picks and sets a blueprint based on reference material and video parameters
 */
export type AutopickType = 'public' | 'custom' | 'recent_video' | 'none';

export interface FetchAutopickBlueprintResult {
    blueprintId: number | null;
    pickType: AutopickType;
    rawResult: BlueprintAutoPickResponse;
    error?: string;
}

/**
 * Core autopick function that calls the API and returns the result.
 * Does NOT dispatch to Redux - use autoPickBlueprintOperation for that.
 * This is exported for use by tools that need autopick without Redux side effects.
 */
export async function fetchAutopickBlueprint({
    templateId,
    narrativeThread,
    inputContent,
}: {
    templateId: string;
    narrativeThread: NarrativeThreadEnum;
    inputContent: string;
}): Promise<FetchAutopickBlueprintResult> {
    try {
        const taskResponse = await ApiService.blueprintAutoPickCreate({
            body: {
                input_content: inputContent,
                template: templateId,
                narrative_thread: narrativeThread,
            },
        });

        const ws = WebSocketClient.getInstance();
        const result = await ws.waitForMessage<BlueprintAutoPickResponse>(
            taskResponse.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'blueprint_auto_pick',
        );

        if (result.public_blueprint_id !== null) {
            return {
                blueprintId: result.public_blueprint_id,
                pickType: 'public',
                rawResult: result,
            };
        }

        if (result.custom_blueprint_id !== null) {
            return {
                blueprintId: result.custom_blueprint_id,
                pickType: 'custom',
                rawResult: result,
            };
        }

        if (result.recent_video_id !== null) {
            // Create blueprint from recent video
            try {
                const { data: blueprint } =
                    await ApiService.customBlueprintsCreateFromSimilarCreate({
                        body: { video_id: result.recent_video_id },
                    });
                return {
                    blueprintId: blueprint.id,
                    pickType: 'recent_video',
                    rawResult: result,
                };
            } catch (error) {
                ErrorReporter.capture(error, {
                    extra: {
                        videoId: result.recent_video_id,
                        context: 'create_blueprint_from_video',
                    },
                });
                // Fall through to return 'none'
            }
        }

        return {
            blueprintId: null,
            pickType: 'none',
            rawResult: result,
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown autopick error';
        ErrorReporter.capture(error, {
            extra: { templateId, narrativeThread, context: 'fetch_autopick_blueprint' },
        });
        return {
            blueprintId: null,
            pickType: 'none',
            rawResult: {
                public_blueprint_id: null,
                custom_blueprint_id: null,
                recent_video_id: null,
            },
            error: errorMessage,
        };
    }
}

export const autoPickBlueprintOperation = ({
    templateId,
    narrativeThread,
    referenceMaterial,
}: {
    templateId: string;
    narrativeThread: NarrativeThreadEnum;
    referenceMaterial: string;
}) => {
    return async (dispatch: AppDispatch) => {
        const result = await fetchAutopickBlueprint({
            templateId,
            narrativeThread,
            inputContent: referenceMaterial,
        });

        if (result.blueprintId !== null) {
            dispatch(setBlueprintId({ blueprintId: result.blueprintId }));

            if (result.pickType === 'recent_video') {
                analytics.track('Video Blueprint Selected', {
                    blueprintId: result.blueprintId,
                    sourceVideoId: result.rawResult.recent_video_id,
                    narrativeThread,
                    templateId,
                });
            }
        }

        dispatch(
            setRecommendedBlueprint({
                recommendedBlueprint: {
                    blueprintId: result.rawResult.public_blueprint_id,
                    customBlueprintId: result.rawResult.custom_blueprint_id,
                    recentVideoId: result.rawResult.recent_video_id,
                },
            }),
        );

        analytics.track('Blueprint Auto Pick', {
            pickedBlueprint: result.pickType !== 'none',
            publicBlueprintId: result.rawResult.public_blueprint_id,
            customBlueprintId: result.rawResult.custom_blueprint_id,
            recentVideoId: result.rawResult.recent_video_id,
            pickType: result.pickType,
            narrativeThread,
            templateId,
        });

        return {
            success: result.error === undefined,
            pickType: result.pickType,
            blueprintId: result.rawResult.public_blueprint_id,
            customBlueprintId: result.rawResult.custom_blueprint_id,
            recentVideoId: result.rawResult.recent_video_id,
        };
    };
};
